import React from 'react';
import { Controller } from 'react-hook-form';
import { TimePicker } from 'antd';
import dayjs from 'dayjs';
import { useScreenView } from '../../../../utils/ScreenViewContext';

const format = 'h:mm A';
const daysOfWeek = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const daysOfWeekMobile = ['Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'];

function HoursOfOperationWidget({
  control,
  hoursOfOperation,
  viewOnly = false,
  addClass,
}: {
  control?: any;
  hoursOfOperation: any;
  viewOnly?: boolean;
  addClass?: string;
}) {
  const { screenView } = useScreenView();

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div
            className={`w-full rounded-[4px] border-[1px] border-bordergray p-3 ${addClass}`}
          >
            <h3 className="mb-4 text-[16px] text-graysecondary">
              {viewOnly ? 'Hours of Operation' : 'Hours of Operation Setting'}
            </h3>
            <div className="grid gap-4">
              {daysOfWeek.map((day, idx) => (
                <div key={day} className="flex items-center gap-4">
                  <div className="w-1/5 font-medium text-gray-700">
                    {daysOfWeekMobile[idx]}
                  </div>

                  {/* Open/Close Status */}
                  <div className="flex w-1/5 items-center">
                    {viewOnly ? (
                      <span className="text-gray-700">
                        {hoursOfOperation[day]?.isOpen ? 'Open' : 'Closed'}
                      </span>
                    ) : (
                      <Controller
                        name={`hoursOfOperation.${day}.isOpen`}
                        control={control}
                        defaultValue={hoursOfOperation[day]?.isOpen || false}
                        render={({ field }) => (
                          <div
                            className="flex cursor-pointer items-center"
                            onClick={() => field.onChange(!field.value)}
                          >
                            <div
                              className={`relative h-5 w-10 rounded-full transition-colors duration-300 ${
                                field.value ? 'bg-yellow-500' : 'bg-gray-300'
                              }`}
                            >
                              <div
                                className={`absolute left-0.5 top-0.5 h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                                  field.value ? 'translate-x-5' : ''
                                }`}
                              />
                            </div>
                            <span className="ml-2 text-gray-700 sm:hidden">
                              {field.value ? 'Open' : 'Closed'}
                            </span>
                          </div>
                        )}
                      />
                    )}
                  </div>

                  {/* Time range selectors */}
                  <div className="flex w-3/5 items-center gap-2">
                    {viewOnly ? (
                      <>
                        <span className="text-gray-700">
                          {hoursOfOperation[day]?.isOpen
                            ? `${hoursOfOperation[day]?.from || '--'} - ${
                                hoursOfOperation[day]?.to || '--'
                              }`
                            : 'Closed'}
                        </span>
                      </>
                    ) : (
                      <>
                        <Controller
                          name={`hoursOfOperation.${day}.from`}
                          control={control}
                          defaultValue={
                            hoursOfOperation[day]?.from
                              ? dayjs(hoursOfOperation[day].from, format)
                              : null
                          }
                          render={({ field }) => (
                            <TimePicker
                              className="w-1/2"
                              {...field}
                              use12Hours
                              format={format}
                              minuteStep={30}
                              disabled={!hoursOfOperation[day]?.isOpen}
                              value={
                                field.value ? dayjs(field.value, format) : null
                              }
                              onChange={(time) =>
                                field.onChange(time ? time.format(format) : '')
                              }
                            />
                          )}
                        />

                        <span className="text-gray-500">-</span>

                        <Controller
                          name={`hoursOfOperation.${day}.to`}
                          control={control}
                          defaultValue={
                            hoursOfOperation[day]?.to
                              ? dayjs(hoursOfOperation[day].to, format)
                              : null
                          }
                          render={({ field }) => (
                            <TimePicker
                              className="w-1/2"
                              {...field}
                              use12Hours
                              format={format}
                              minuteStep={30}
                              disabled={!hoursOfOperation[day]?.isOpen}
                              value={
                                field.value ? dayjs(field.value, format) : null
                              }
                              onChange={(time) =>
                                field.onChange(time ? time.format(format) : '')
                              }
                            />
                          )}
                        />
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default HoursOfOperationWidget;
